import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  SaveDraftRequest,
  SubmitTranslationDraft,
  TranslationDraft,
} from '@internal/backstage-plugin-i18n-dictionary-manager-common';

/**
 * API surface exposed by the i18n-dictionary-manager frontend plugin.
 *
 * @public
 */
export interface I18nDictionaryManagerApi {
  /** Fetch the current draft for a system + lifecycle combination. Returns null if none exists. */
  getDraft(
    systemEntityRef: string,
    lifecycle: string,
  ): Promise<TranslationDraft | null>;

  /** Upsert (create or update) the draft for one language within a lifecycle. */
  saveDraft(request: SaveDraftRequest): Promise<void>;

  /** Mark the draft as submitted, recording the PR details. */
  submitDraft(
    systemEntityRef: string,
    lifecycle: string,
    payload: SubmitTranslationDraft,
  ): Promise<void>;

  /** Discard the draft for a system + lifecycle. */
  deleteDraft(systemEntityRef: string, lifecycle: string): Promise<void>;
}

/**
 * ApiRef for the i18n-dictionary-manager API.
 *
 * @public
 */
export const i18nDictionaryManagerApiRef = createApiRef<I18nDictionaryManagerApi>({
  id: 'plugin.i18n-dictionary-manager.api',
});

/**
 * Default implementation of {@link I18nDictionaryManagerApi}.
 *
 * @public
 */
export class I18nDictionaryManagerApiClient implements I18nDictionaryManagerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('i18n-dictionary-manager');
  }

  async getDraft(
    systemEntityRef: string,
    lifecycle: string,
  ): Promise<TranslationDraft | null> {
    const base = await this.baseUrl();
    const params = new URLSearchParams({ systemEntityRef, lifecycle });
    const response = await this.fetchApi.fetch(`${base}/drafts?${params}`);
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }

  async saveDraft(request: SaveDraftRequest): Promise<void> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${base}/drafts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }

  async submitDraft(
    systemEntityRef: string,
    lifecycle: string,
    payload: SubmitTranslationDraft,
  ): Promise<void> {
    const base = await this.baseUrl();
    const params = new URLSearchParams({ systemEntityRef, lifecycle });
    const response = await this.fetchApi.fetch(
      `${base}/drafts/submit?${params}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }

  async deleteDraft(systemEntityRef: string, lifecycle: string): Promise<void> {
    const base = await this.baseUrl();
    const params = new URLSearchParams({ systemEntityRef, lifecycle });
    const response = await this.fetchApi.fetch(
      `${base}/drafts?${params}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }
}
